/**
 * Property Model for FreshCards
 * Property definitions and value types
 */

export interface PropertyDefinition {
  id: string;
  name: string;
  type: PropertyType;
  visible: boolean;
  position: number;
  options?: SelectOption[];
  statusOptions?: StatusOption[];
  numberFormat?: NumberFormat;
  prefix?: string;
  suffix?: string;
  dateFormat?: string;
  relatedDatabaseId?: string;
  formula?: string;
  readOnly?: boolean;
  defaultValue?: unknown;
}

export type PropertyType = 
  | 'title' | 'text' | 'number' | 'select' | 'multi_select'
  | 'date' | 'checkbox' | 'url' | 'files' | 'relation'
  | 'formula' | 'status' | 'person' | 'phone' | 'email'
  | 'image' | 'created_time' | 'last_edited_time';

export interface PropertyValue {
  propertyId: string;
  value: unknown;
}

export interface SelectOption {
  id: string;
  name: string;
  color: string;
}

export interface StatusOption extends SelectOption {
  state: 'active' | 'completed' | 'cancelled';
}

export interface NumberFormat {
  type: 'number' | 'currency' | 'percent';
  symbol?: string;
  decimals?: number;
}

export interface Filter {
  propertyId: string;
  operator: FilterOperator;
  value: unknown;
}

export type FilterOperator = 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than';

export interface Sort {
  propertyId: string;
  direction: 'asc' | 'desc';
}

/**
 * Creates a new property with default values
 */
export function createProperty(
  id: string,
  name: string,
  type: PropertyType,
  position: number = 0
): PropertyDefinition {
  return {
    id,
    name,
    type,
    visible: true,
    position,
  };
}

/**
 * Creates a title property
 */
export function createTitleProperty(id: string = 'title'): PropertyDefinition {
  return createProperty(id, 'Title', 'title', 0);
}

/**
 * Creates a text property
 */
export function createTextProperty(id: string, name: string, position: number = 0): PropertyDefinition {
  return createProperty(id, name, 'text', position);
}

/**
 * Creates a select property with options
 */
export function createSelectProperty(
  id: string,
  name: string,
  options: Array<{ name: string; color: string }>,
  position: number = 0
): PropertyDefinition {
  return {
    ...createProperty(id, name, 'select', position),
    options: options.map((opt, i) => ({
      id: `${id}_opt_${i}`,
      name: opt.name,
      color: opt.color,
    })),
  };
}

/**
 * Creates a number property with format
 */
export function createNumberProperty(
  id: string,
  name: string,
  format: NumberFormat = { type: 'number' },
  position: number = 0
): PropertyDefinition {
  return {
    ...createProperty(id, name, 'number', position),
    numberFormat: format,
  };
}

/**
 * Creates a date property
 */
export function createDateProperty(
  id: string,
  name: string,
  dateFormat: string = 'YYYY-MM-DD',
  position: number = 0
): PropertyDefinition {
  return {
    ...createProperty(id, name, 'date', position),
    dateFormat,
  };
}

/**
 * Creates a status property with options
 */
export function createStatusProperty(
  id: string,
  name: string,
  statusOptions: Array<{ name: string; color: string; state: 'active' | 'completed' | 'cancelled' }>,
  position: number = 0
): PropertyDefinition {
  return {
    ...createProperty(id, name, 'status', position),
    statusOptions: statusOptions.map((opt, i) => ({
      id: `${id}_status_${i}`,
      name: opt.name,
      color: opt.color,
      state: opt.state,
    })),
  };
}